/* DeepDiveRevenueReport — the "Boom" moment.
 *
 * Replaces the old "Issues Found" table in RoyAI Section 03 with a live
 * per-platform revenue table: Platform | Streams | Live Since | Total
 * Revenue | Your Share. Data comes from /api/royai/revenue-report which
 * is populated whenever a user runs the Smart Audit on a song link.
 */
import { useState } from "react";
import { CheckCircle2, TrendingUp, Music2, ExternalLink, Trash2, Users, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { royai, fmtUsdCents } from "@/lib/royai";

const nfInt = new Intl.NumberFormat("en-US");

function PlatformDot({ color }) {
  return <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: color }} />;
}

function TrackReportCard({ track, onDelete, onSplitChanged }) {
  const [userPct, setUserPct] = useState(Number(track.user_share_pct || 25));
  const [featured, setFeatured] = useState(Number(track.featured_share_pct || 0));
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const totalRev = Number(track.total_revenue_usd || 0);
  const userShare = Math.round(totalRev * userPct) / 100;
  const featuredShare = Math.round(totalRev * featured) / 100;
  const remaining = Math.round(totalRev * 100 - userShare * 100 - featuredShare * 100) / 100;
  const overBudget = featured + userPct > 100;

  async function saveFeatured(v) {
    if (v + userPct > 100) {
      toast.error(`Featured + your ${userPct.toFixed(1)}% can't exceed 100%.`);
      return;
    }
    setSaving(true);
    try {
      await royai.updateTrackSplit(track.track_id, { featured_share_pct: v });
      onSplitChanged && await onSplitChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Couldn't save split.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function saveUserPct(v) {
    if (v + featured > 100) {
      toast.error(`Your share + featured (${featured.toFixed(1)}%) can't exceed 100%.`);
      return;
    }
    setUserPct(v);
    setSaving(true);
    try {
      await royai.updateTrackSplit(track.track_id, { user_share_pct: v });
      onSplitChanged && await onSplitChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Couldn't save split.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function downloadPdf() {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/royai/revenue-report/${track.track_id}/split-report.pdf`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `RoyAI Split Report - ${(track.title || "track")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    } catch {
      toast.error("Could not download the split report.");
    } finally {
      setDownloading(false);
    }
  }
  return (
    <div className="border border-amber-500/20 bg-gradient-to-b from-amber-500/[0.03] to-black/40 p-5 lg:p-6 mb-4" data-testid={`revenue-track-${track.track_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Music2 size={14} className="text-amber-400" />
            <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">
              Deep Dive · {track.provider || "audit"}
            </span>
            {track.release_year && (
              <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">
                · Live since {track.release_year}
              </span>
            )}
            {track.isrc && (
              <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">
                · ISRC {track.isrc}
              </span>
            )}
          </div>
          <div className="font-display text-xl text-white">
            {track.artist ? `${track.artist} — ${track.title}` : track.title}
          </div>
          {(track.role_label || track.rows?.[0]?.role_label) && (
            <div className="text-xs font-mono-tech uppercase tracking-widest text-amber-400 mt-0.5">
              Role · {track.role_label || track.rows?.[0]?.role_label}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">Your Share ({userPct.toFixed(1)}%)</div>
          <div className="font-display text-3xl text-amber-400" data-testid="track-your-share">{fmtUsdCents(userShare)}</div>
          <div className="text-xs text-neutral-500 mt-1">of {fmtUsdCents(totalRev)} total</div>
        </div>
      </div>

      <div className="overflow-x-auto -mx-5 lg:-mx-6">
        <table className="w-full text-sm min-w-[720px]" data-testid="revenue-report-table">
          <thead>
            <tr className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 border-y border-white/10">
              <th className="text-left  px-5 lg:px-6 py-2 font-normal">Platform</th>
              <th className="text-right px-3 py-2 font-normal">Streams</th>
              <th className="text-right px-3 py-2 font-normal">Live Since</th>
              <th className="text-right px-3 py-2 font-normal">Total Revenue</th>
              <th className="text-right px-3 py-2 font-normal">Your Share</th>
              <th className="text-right px-5 lg:px-6 py-2 font-normal">Status</th>
            </tr>
          </thead>
          <tbody>
            {(track.rows || []).map((row) => {
              const isEstimated = row.estimated === true;
              return (
              <tr
                key={row.id}
                className={`border-b border-white/5 hover:bg-white/[0.02] ${isEstimated ? "bg-amber-500/[0.04]" : ""}`}
                data-testid={`revenue-row-${row.platform.toLowerCase().replace(/\s+/g,'-')}`}
              >
                <td className="px-5 lg:px-6 py-3 text-white">
                  <PlatformDot color={row.platform_color} />{row.platform}
                </td>
                <td className={`px-3 py-3 text-right font-mono-tech ${isEstimated ? "text-amber-300" : "text-neutral-300"}`}>
                  {nfInt.format(row.streams || 0)}
                </td>
                <td className="px-3 py-3 text-right font-mono-tech text-neutral-400">{row.live_since || "—"}</td>
                <td className={`px-3 py-3 text-right font-mono-tech ${isEstimated ? "text-amber-300" : "text-neutral-300"}`}>
                  {fmtUsdCents(row.total_revenue_usd)}
                </td>
                <td className={`px-3 py-3 text-right font-mono-tech ${isEstimated ? "text-amber-300" : "text-amber-400"}`}>
                  {fmtUsdCents(row.user_share_usd)}
                </td>
                <td className="px-5 lg:px-6 py-3 text-right">
                  {isEstimated ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5"
                      title={row.source_hint || "Estimated projection"}
                      data-testid={`row-badge-estimated-${row.platform.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      <Sparkles size={10} /> Estimated
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5"
                      title={row.source_hint || "Verified from source"}
                      data-testid={`row-badge-verified-${row.platform.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      <CheckCircle2 size={10} /> Verified
                    </span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-2 flex items-center gap-4 text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
        <span className="flex items-center gap-1.5"><CheckCircle2 size={10} className="text-emerald-400" /> Verified — from source</span>
        <span className="flex items-center gap-1.5"><Sparkles size={10} className="text-amber-400" /> Estimated — projected from market ratios</span>
      </div>

      {track.source_url && (
        <div className="mt-3 flex justify-end">
          <a href={track.source_url} target="_blank" rel="noreferrer"
             className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 hover:text-amber-400 flex items-center gap-1">
            View source <ExternalLink size={10} />
          </a>
        </div>
      )}

      {/* Royalty Split Breakdown */}
      <div className="mt-5 pt-5 border-t border-white/5" data-testid={`split-breakdown-${track.track_id}`}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-amber-400" />
            <div>
              <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-amber-400">Royalty Split Breakdown</div>
              <div className="text-xs text-neutral-500">Fair math for you and your collaborators.</div>
            </div>
          </div>
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 border border-amber-500/40 bg-amber-500/[0.06] text-amber-300 hover:bg-amber-500/[0.12] px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-[0.22em] font-semibold transition-colors disabled:opacity-60"
            data-testid={`split-download-${track.track_id}`}
          >
            <Download size={12} /> {downloading ? "Preparing…" : "Download Split Report"}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-3">
          <div className="bg-black/40 px-3 py-3">
            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Total Revenue</div>
            <div className="font-mono-tech text-lg text-white">{fmtUsdCents(totalRev)}</div>
            <div className="text-[10px] text-neutral-500">100%</div>
          </div>
          <div className="bg-black/40 px-3 py-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Your Share</div>
              <input
                type="number"
                min={0}
                max={Math.max(0, 100 - featured)}
                step={0.5}
                value={userPct}
                onChange={(e) => saveUserPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                disabled={saving}
                className="w-14 bg-black border border-white/10 focus:border-amber-500 outline-none px-1.5 py-0.5 text-[11px] font-mono-tech text-amber-400 text-right"
                data-testid={`user-share-input-${track.track_id}`}
                aria-label="Your ownership share percentage"
              />
            </div>
            <div className="font-mono-tech text-lg text-amber-400" data-testid={`user-share-usd-${track.track_id}`}>{fmtUsdCents(userShare)}</div>
            <input
              type="range"
              min={0}
              max={Math.max(0, 100 - featured)}
              step={0.5}
              value={userPct}
              onChange={(e) => setUserPct(Math.max(0, Math.min(100, Number(e.target.value))))}
              onMouseUp={(e) => saveUserPct(Number(e.target.value))}
              onTouchEnd={(e) => saveUserPct(Number(e.target.value))}
              disabled={saving}
              className="w-full mt-2 accent-amber-500 cursor-pointer disabled:opacity-50"
              data-testid={`user-share-slider-${track.track_id}`}
              aria-label="Your ownership share slider"
            />
            <div className="text-[10px] text-neutral-500">
              {userPct.toFixed(1)}% · {track.user_share_overridden ? "custom" : "from assessment"}
            </div>
          </div>
          <div className="bg-black/40 px-3 py-3">
            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Featured Artist</div>
            <div className={`font-mono-tech text-lg ${overBudget ? "text-rose-400" : "text-emerald-400"}`} data-testid={`featured-share-${track.track_id}`}>
              {fmtUsdCents(featuredShare)}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <input
                type="number"
                min={0}
                max={Math.max(0, 100 - userPct)}
                step={0.5}
                value={featured}
                onChange={(e) => setFeatured(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                onBlur={(e) => saveFeatured(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                disabled={saving}
                className="w-16 bg-black border border-white/10 focus:border-amber-500 outline-none px-2 py-1 text-xs font-mono-tech text-white text-right"
                data-testid={`featured-input-${track.track_id}`}
                aria-label="Featured artist share percentage"
              />
              <span className="text-[10px] font-mono-tech text-neutral-500">% share</span>
            </div>
          </div>
          <div className="bg-black/40 px-3 py-3">
            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Remaining Balance</div>
            <div className={`font-mono-tech text-lg ${remaining < 0 ? "text-rose-400" : "text-white"}`}>{fmtUsdCents(remaining)}</div>
            <div className="text-[10px] text-neutral-500">{(100 - userPct - featured).toFixed(1)}%</div>
          </div>
        </div>
        {overBudget && (
          <div className="text-[11px] text-rose-300 border border-rose-500/40 bg-rose-500/5 px-3 py-2">
            Your share ({userPct.toFixed(1)}%) + featured ({featured.toFixed(1)}%) exceeds 100%. Lower one of them.
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
          Was this a test link?
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-[0.22em] text-emerald-400 border border-emerald-500/30 bg-emerald-500/[0.05] px-3 py-1.5"
                data-testid={`track-keep-${track.track_id}`}>
            <CheckCircle2 size={12} /> Kept
          </span>
          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="inline-flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-[0.22em] text-rose-300 hover:text-rose-200 border border-rose-500/40 bg-rose-500/[0.05] hover:bg-rose-500/10 px-3 py-1.5 transition-colors"
              data-testid={`track-delete-${track.track_id}`}
            >
              <Trash2 size={12} /> Delete audit
            </button>
          ) : (
            <div className="flex items-center gap-2" data-testid={`track-delete-confirm-${track.track_id}`}>
              <span className="text-[10px] font-mono-tech uppercase tracking-widest text-rose-300">Delete this audit?</span>
              <button
                type="button"
                onClick={async () => {
                  if (deleting) return;
                  setDeleting(true);
                  try {
                    await onDelete?.(track);
                  } finally {
                    setDeleting(false);
                    setConfirmingDelete(false);
                  }
                }}
                disabled={deleting}
                className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-white bg-rose-600 hover:bg-rose-500 px-3 py-1.5 disabled:opacity-60 transition-colors"
                data-testid={`track-delete-confirm-yes-${track.track_id}`}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-neutral-300 hover:text-white border border-white/10 px-3 py-1.5 transition-colors"
                data-testid={`track-delete-confirm-no-${track.track_id}`}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeepDiveRevenueReport({ report, onDeleteTrack, onSplitChanged }) {
  const tracks = report?.tracks || [];
  const totals = report?.totals || {};
  return (
    <div data-testid="deep-dive-revenue-report">
      {/* Section KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-4">
        <div className="bg-[#0c0c0c] p-4">
          <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-1">Tracks Audited</div>
          <div className="font-display text-2xl text-white" data-testid="rr-track-count">{totals.track_count || 0}</div>
        </div>
        <div className="bg-[#0c0c0c] p-4">
          <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-1">Total Revenue</div>
          <div className="font-display text-2xl text-white" data-testid="rr-total-revenue">{fmtUsdCents(totals.total_revenue_usd || 0)}</div>
        </div>
        <div className="bg-[#0c0c0c] p-4">
          <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-1">Your Share</div>
          <div className="font-display text-2xl text-emerald-400" data-testid="rr-your-share">{fmtUsdCents(totals.your_share_usd || 0)}</div>
        </div>
        <div className="bg-[#0c0c0c] p-4">
          <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-1">Unclaimed</div>
          <div className="font-display text-2xl text-amber-400 flex items-center gap-2" data-testid="rr-unclaimed">
            <TrendingUp size={16} /> {fmtUsdCents(totals.unclaimed_share_usd || 0)}
          </div>
        </div>
      </div>

      {tracks.length === 0 ? (
        <div className="border border-dashed border-white/10 bg-black/40 p-8 text-center">
          <div className="text-sm text-neutral-400 mb-1">No revenue reports yet.</div>
          <div className="text-xs text-neutral-500">
            Paste a MusicBrainz / Muso.AI / AllMusic / Genius URL above and click <span className="text-amber-400">Run RoyAI Audit</span> to generate your first per-platform revenue report.
          </div>
        </div>
      ) : (
        tracks.map((t) => <TrackReportCard key={t.track_id} track={t} onDelete={onDeleteTrack} onSplitChanged={onSplitChanged} />)
      )}
    </div>
  );
}
